// packages/core/src/learning/schools/fintech-automation.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored to match boglehead-indexing.ts (the reference exhibit).
//
// Voice reminder: neutral enough to be trustworthy, opinionated enough to be
// memorable. Frame beliefs as "[School] believes…". Never invent quotes,
// never tell users what to buy, never copy book/creator text.
//
// ID-bound fields (deferred to the post-authoring cross-reference pass):
//   - figures:            [] (none for this school)
//   - featuredLessonIds:  [] until referenced lessons exist
//   - debateIds:          [] until referenced debates exist
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FINTECH_AUTOMATION_EXHIBIT: SchoolExhibitEntry = {
	id: 'fintech-automation',
	tagline:
		'Fintech and automation believes systems help, but attention still matters.',
	worldviewSummary:
		'Fintech and automation is paying attention to the way apps, dashboards, autopay, transfers, reminders, and subscriptions shape your money behavior. This lens protects you from relying on memory, vibes, and last minute willpower to manage recurring decisions. It accepts a real tradeoff: automation can reduce friction, but it can also hide problems until the damage is already moving. The blind spot is thinking the app is the discipline. Automation works best when it supports clear rules, regular review, and real awareness.',
	coreBeliefs: [
		'Fintech and automation believes good systems reduce avoidable money mistakes.',
		'Fintech and automation believes autopay can protect you from missed bills, but not from bad spending.',
		'Fintech and automation believes dashboards are useful only when they make behavior clearer.',
		'Fintech and automation believes subscriptions need review because silent charges train you to ignore leaks.',
		'Fintech and automation believes automation should serve your plan, not replace your judgment.',
		'Fintech and automation believes convenience has a cost when it makes money feel invisible.',
	],
	warnsAgainst: [
		'Automating bills, transfers, or subscriptions without checking whether the setup still fits your life.',
		'Treating an app balance, dashboard, or alert as the same thing as financial control.',
		'Letting convenience make spending feel unreal.',
		'Using automation to avoid looking at money instead of using it to stay consistent.',
	],
	helps: 'This lens helps adults who want cleaner systems for bills, savings, tracking, subscriptions, and recurring money habits. It may not fit when someone expects apps to create discipline without rules, review, or attention.',
	figures: [],
	canon: [
		{
			title: 'Autopay with review',
			oneLineIdea:
				'Automation can prevent missed payments, but review prevents autopilot mistakes.',
		},
		{
			title: 'The subscription audit',
			oneLineIdea:
				'Small silent charges deserve attention because they can drain money without creating a decision moment.',
		},
		{
			title: 'Dashboard reality check',
			oneLineIdea:
				'A dashboard is valuable when it shows what changed, what matters, and what needs a decision.',
		},
		{
			title: 'Friction by design',
			oneLineIdea:
				'The easiest money flow is not always the healthiest one. Sometimes a pause protects you.',
		},
		{
			title: 'Rules before automation',
			oneLineIdea:
				'The system works better when you decide the rule before the app repeats it.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-fintech-automation',
	debateIds: [],
	applyActions: [
		{
			label: 'Look at your Dashboard before trusting automation',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how a recurring charge changes your future options',
			action: {
				type: 'openSimulator',
				label: 'Simulate the charge',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check whether automation is building more runway',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write the rules your money apps should follow',
			action: {
				type: 'noToolAction',
				label: 'Write your automation rules',
				prompt: 'Write which bills, transfers, subscriptions, and reminders should be automated, then write when you will review them.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Automation can save your money life, or quietly hide the leak.',
			kind: 'hook',
		},
		{
			line: 'The app is not the discipline. The rule behind the app is.',
			kind: 'hook',
		},
		{
			line: 'What subscription did you forget about until it had already cost you real money?',
			kind: 'interview-question',
		},
		{
			line: 'Which money task became easier once you stopped relying on memory?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches people to use automation without turning their brain off.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Modern money moves fast. Your systems need to make it visible again.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how autopay helps with bills but fails when spending rules are unclear.',
			kind: 'creator-prompt',
		},
		{
			line: 'Audit five subscriptions on camera and ask which ones still match the person you are becoming.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
